'use client';

/**
 * Dependency imports
 */
import React from 'react';
import {
  Anchor,
  Box,
  Container,
  Grid,
  GridCol,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import Image from 'next/image';

/**
 * Local imports
 */
import SiteHeader from '@/components/SiteHeader';
import PageTitle from '@/components/PageTitle';
import HelpChangeSection from '@/components/HelpChangeSection';
import SiteFooter from '@/components/SiteFooter';
import Clients from '@/components/Clients';
import { useRender } from '@/hooks/use-render';
import { useResponsive } from '@/hooks/use-responsive';
import EmailIcon from '../../../../public/img/icons/email.svg';
import PhoneIcon from '../../../../public/img/icons/phone.svg';
import MarkerIcon from '../../../../public/img/icons/marker.svg';
import ContactForm from '@/components/ContactForm';
import GoogleMap from '@/components/GoogleMap';

/**
 * ContactPage Component
 */
const ContactPage = () => {
  const { responsive } = useResponsive();
  const render = useRender({ smoothing: true });
  const [isSent, setIsSent] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [formError, setFormError] = React.useState('');
  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      reason: '',
      message: '',
      termsOfService: false,
    },

    validate: {
      firstName: (value: string) => !value && 'This field is required',
      lastName: (value: string) => !value && 'This field is required',
      email: (value: string) => {
        if (!value) return 'This field is required';
        if (!/^\S+@\S+$/.test(value)) return 'Invalid email';
      },
      phone: (value: string) => {
        if (value && !/^\+?(\d[\d\- ]{7,}\d)$/.test(value))
          return 'Invalid phone number';
      },
      reason: (value: string) => !value && 'This field is required',
      message: (value: string) => !value && 'This field is required',
      termsOfService: (value: boolean) =>
        !value && 'You must agree to the terms and conditions',
    },
  });

  const handleFormSubmit = (values: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    reason: string;
    message: string;
    termsOfService: boolean;
  }) => {
    const controller = new AbortController();
    const signal = controller.signal;

    setIsSubmitting(true);
    setFormError('');

    const { grecaptcha } = window as any;

    if (grecaptcha) {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
            action: 'submit',
          })
          .then(async (token: string) => {
            const response = await fetch('/api/contact', {
              method: 'POST',
              body: JSON.stringify({
                ...values,
                token,
              }),
              signal,
            });

            setIsSubmitting(false);

            if (response.status !== 200) {
              setFormError('An error occurred. Please try again later.');
              return;
            }

            const responseData = await response.json();

            if (responseData.error) {
              setFormError(responseData.error);
              return;
            }

            setIsSent(true);
          });
      });
    }
  };

  return render(
    // Fixed content
    <SiteHeader scheme="dark" />,

    // Page content
    <main>
      <PageTitle
        subtitle="Contact us"
        title="Get in touch"
        tagline="We're here to help. Reach out for any inquiries or support."
        pt="25vh"
        pb="15vh"
        bg="gray.0"
      />

      <Box py={100}>
        <Container size="xl">
          <Group
            wrap="nowrap"
            align="stretch"
            gap={responsive({
              xs: 25,
              lg: 50,
              xl: 100,
            })}
            style={{
              flexDirection: responsive({
                xs: 'column',
                lg: 'row',
              }),
            }}
          >
            <Box flex={1}>
              <Title size={42} fw={700} mb={40}>
                Contact info
              </Title>

              <Grid columns={2} gutter={50}>
                <GridCol
                  span={responsive({
                    xs: 2,
                    sm: 1,
                  })}
                >
                  <Stack gap={15} align="start">
                    <Box w={42}>
                      <EmailIcon className="icon-shadow" />
                    </Box>

                    <Box>
                      <Title size={18} fw={500} mb={5}>
                        Email
                      </Title>

                      <Text>Send us an email today!</Text>
                    </Box>

                    <Anchor
                      href="mailto:info@homehealthsolutions.io"
                      c="gray.7"
                      underline="always"
                    >
                      info@homehealthsolutions.io
                    </Anchor>
                  </Stack>
                </GridCol>

                <GridCol
                  span={responsive({
                    xs: 2,
                    sm: 1,
                  })}
                >
                  <Stack gap={15} align="start">
                    <Box w={42}>
                      <PhoneIcon className="icon-shadow" />
                    </Box>

                    <Box>
                      <Title size={18} fw={500} mb={5}>
                        Phone
                      </Title>

                      <Text>Give us a call now!</Text>
                    </Box>

                    <Anchor
                      href="tel:+14036814663"
                      c="gray.7"
                      underline="always"
                    >
                      +1 (403) 681-4663
                    </Anchor>
                  </Stack>
                </GridCol>

                <GridCol span={2}>
                  <Stack gap={15} align="start">
                    <Box w={42}>
                      <MarkerIcon className="icon-shadow" />
                    </Box>

                    <Box>
                      <Title size={18} fw={500} mb={5}>
                        Main Office
                      </Title>

                      <Text>
                        7909 Flint Rd SW Suit 201 Calgary, Alberta, T2H1G3
                      </Text>
                    </Box>

                    <Anchor
                      href="https://www.google.com/maps/dir/?api=1&destination=7909+Flint+Rd+SW+Suite+201+Calgary,+Alberta,+T2H1G3"
                      c="gray.7"
                      underline="never"
                    >
                      Get Directions
                    </Anchor>
                  </Stack>
                </GridCol>

                <GridCol span={2}>
                  <GoogleMap 
                    address="7909 Flint Rd SW Suite 201 Calgary, Alberta, T2H1G3"
                    apiKey={process.env.NEXT_PUBLIC_GOOGLE_API_KEY!}
                    height="400px"
                  />
                </GridCol>
              </Grid>
            </Box>

            <Box flex={1}>
              <Title size={42} fw={700} mb={40}>
                Contact form
              </Title>

              <Text fz={18} c="gray.7" mb={40}>
              Got a question or just want to say hi? Drop us a line below. We&lsquo;d love to hear from you!              </Text>
              <ContactForm
                onSubmitSuccess={() => {
                  // Optional: Any additional logic you want to run after successful submission
                  // console.log('Form submitted successfully');
                }}
              />
            </Box>
          </Group>
        </Container>
      </Box>

      <Clients bg="gray.0" />

      <HelpChangeSection />

      <SiteFooter />
    </main>
  );
};


export default ContactPage;
